import { type CancelTokenSource } from 'axios'
import { type IReactionDisposer, makeAutoObservable } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type ComplianceLayerStore } from 'pages/settings/pages/compliance/store/complianceLayerStore'
import { CallingRoutesSteps } from 'pages/settings/pages/compliance/pages/calling/route/type'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { StepsStore } from 'pages/settings/store/stepsStore'
import { VoiceIntegrityStore } from './voiceIntegrityStore'
import { StirShakenStore } from './StirShakenStore'
import { CnamCallerIdStore } from './cnamCallerIdStore'

const titleMap: Record<string, string> = {
  [CallingRoutesSteps.enableVoiceIntegrity]: 'Enable Voice Integrity',
  [CallingRoutesSteps.companyInfo]: 'Company info',
  [CallingRoutesSteps.enableStirShaken]: 'Enable STIR / SHAKEN',
  [CallingRoutesSteps.enableCnamCallerId]: 'Enable CNAM Caller ID',
  [CallingRoutesSteps.configure]: 'Configure',
}

const layoutTitleMap: Record<string, string> = {
  [CallingRoutesSteps.enableVoiceIntegrity]: 'Voice Integrity',
  [CallingRoutesSteps.companyInfo]: 'Voice Integrity',
  [CallingRoutesSteps.enableStirShaken]: 'STIR / SHAKEN',
  [CallingRoutesSteps.enableCnamCallerId]: 'CNAM Caller ID',
  [CallingRoutesSteps.configure]: 'CNAM Caller ID',
}

export class CallingStepsStore {
  private readonly _voiceIntegrityStore: VoiceIntegrityStore
  private readonly _stirShakenStore: StirShakenStore
  private readonly _cnamCallerIdStore: CnamCallerIdStore

  private _disposePageReaction: IReactionDisposer | null = null
  private _cancelPageSource: CancelTokenSource | null = null

  private _stepsStore = new StepsStore()

  constructor(
    private _complianceLayerStore: ComplianceLayerStore,
    private _entities: IEntitiesStore
  ) {
    this._voiceIntegrityStore = new VoiceIntegrityStore(this, this._entities)
    this._stirShakenStore = new StirShakenStore(this, this._entities)
    this._cnamCallerIdStore = new CnamCallerIdStore(this, this._entities)

    makeAutoObservable(this)
  }

  get voiceIntegrityStore() {
    return this._voiceIntegrityStore
  }

  get stirShakenStore() {
    return this._stirShakenStore
  }

  get cnamCallerIdStore() {
    return this._cnamCallerIdStore
  }

  get stepsStore() {
    return this._stepsStore
  }

  get complianceLayerStore() {
    return this._complianceLayerStore
  }

  dispose = () => {
    this._disposePageReaction?.()
    this._cancelPageSource?.cancel()
  }

  getProgressInfoTitle = (pathname: CallingRoutesSteps) => {
    return titleMap[pathname] || ''
  }

  getLayoutTitle = (pathname: CallingRoutesSteps) => {
    return layoutTitleMap[pathname] || ''
  }

  closeSetting = () => {
    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}`,
    })
  }

  decreaseStep = (path: CallingRoutesSteps) => {
    let newPath

    if (path === CallingRoutesSteps.companyInfo) {
      newPath = CallingRoutesSteps.enableVoiceIntegrity
    }
    if (path === CallingRoutesSteps.configure) {
      newPath = CallingRoutesSteps.enableCnamCallerId
    }

    if (!newPath) return

    this.stepsStore.setStep(1)

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}/${newPath}`,
    })
  }
}
